import {
  TravelerProfile,
  TravelerProfileDetailsUpdate,
  TravelerProfileSuggestions,
  TravelerProfileSuggestionsPipeline,
  TravelerProfileSuggestionsResponse,
  TravelPreferenceSections,
} from '@marageti/z4-sdk/lib/people';
import { TRAVEL_PREFERENCE_KEYS } from '../../../components/traveler-profile-preferences/utils';
import {
  SuggestionEntry,
  SuggestionLocator,
  SuggestionStatus,
  SuggestionTextField,
  SuggestionsState,
  TravelPreferenceSubKey,
} from './types';

const TEXT_FIELDS: readonly SuggestionTextField[] = ['loyaltyPrograms', 'giftsReceived', 'householdDetails'] as const;

const isEmptyString = (value: string | undefined): boolean => !value || value.trim() === '';

const hasAnyPreferenceSet = (prefs: TravelPreferenceSections | undefined): boolean => {
  if (!prefs) return false;
  return TRAVEL_PREFERENCE_KEYS.some((k) => !isEmptyString(prefs[k]));
};

/**
 * True when the traveler profile has no AI-fillable content populated yet
 * and is therefore eligible for the suggestion entry alert.
 */
export const profileIsGatedEmpty = (profile: TravelerProfile | undefined): boolean => {
  if (!profile) return false;
  return (
    !hasAnyPreferenceSet(profile.travelPreferences)
    && (profile.significantDates?.length ?? 0) === 0
    && isEmptyString(profile.loyaltyPrograms)
    && isEmptyString(profile.giftsReceived)
    && isEmptyString(profile.householdDetails)
  );
};

/**
 * Validates the MCP envelope, throws on `isError` (using the first non-empty
 * text content block as the message), and returns the inner pipeline payload.
 * Returns `undefined` if the tool returned no structured content at all.
 */
export const unwrapPipeline = (
  response: TravelerProfileSuggestionsResponse,
): TravelerProfileSuggestionsPipeline | undefined => {
  if (response.isError) {
    const message = response.content
      ?.map((c) => c.text?.trim())
      .find((t) => !!t) ?? 'AI profile suggestions are unavailable right now.';
    throw new Error(message);
  }
  return response.structuredContent ?? undefined;
};

export const emptySuggestionsState = (): SuggestionsState => ({
  travelPreferences: {},
  text: {},
});

/**
 * True when no sub-store carries an entry. Used by orchestration to detect
 * the "tool succeeded but produced nothing actionable" branch.
 */
export const isSuggestionsStateEmpty = (state: SuggestionsState): boolean => (
  !state.significantDates
  && Object.keys(state.travelPreferences).length === 0
  && Object.keys(state.text).length === 0
);

/**
 * Returns a new entry with `status` set to `to`, but only if the existing
 * entry's status is `from`. The explicit `from` check keeps each transition
 * idempotent and prevents accidental re-renders when the entry isn't in
 * the expected state.
 */
const transitionEntry = <V>(
  entry: SuggestionEntry<V> | undefined,
  from: SuggestionStatus,
  to: SuggestionStatus,
): SuggestionEntry<V> | undefined => {
  if (!entry || entry.status !== from) return entry;
  return { ...entry, status: to };
};

/**
 * Returns a new `SuggestionsState` with the entry at `locator` transitioned
 * from `from` to `to`. Returns the same `state` reference when nothing
 * changed so the reducer can produce a stable identity.
 */
export const transitionSuggestion = (
  state: SuggestionsState,
  locator: SuggestionLocator,
  from: SuggestionStatus,
  to: SuggestionStatus,
): SuggestionsState => {
  if (locator.kind === 'travelPreference') {
    const next = transitionEntry(state.travelPreferences[locator.subKey], from, to);
    if (next === state.travelPreferences[locator.subKey]) return state;
    return {
      ...state,
      travelPreferences: { ...state.travelPreferences, [locator.subKey]: next },
    };
  }
  if (locator.kind === 'text') {
    const next = transitionEntry(state.text[locator.field], from, to);
    if (next === state.text[locator.field]) return state;
    return {
      ...state,
      text: { ...state.text, [locator.field]: next },
    };
  }
  const next = transitionEntry(state.significantDates, from, to);
  if (next === state.significantDates) return state;
  return { ...state, significantDates: next };
};

/**
 * Mass-accept helper for the `Apply all` FAB. Walks every sub-store and
 * promotes pending entries to accepted. The provider computes this once
 * and hands the result to the APPLY_ALL action so the reducer stores a
 * single, deterministic post-accept snapshot instead of recomputing.
 */
export const acceptAllPending = (state: SuggestionsState): SuggestionsState => {
  const text: SuggestionsState['text'] = {};
  (Object.keys(state.text) as SuggestionTextField[]).forEach((k) => {
    text[k] = transitionEntry(state.text[k], 'pending', 'accepted');
  });
  const travelPreferences: SuggestionsState['travelPreferences'] = {};
  (Object.keys(state.travelPreferences) as TravelPreferenceSubKey[]).forEach((k) => {
    travelPreferences[k] = transitionEntry(state.travelPreferences[k], 'pending', 'accepted');
  });
  return {
    text,
    travelPreferences,
    significantDates: transitionEntry(state.significantDates, 'pending', 'accepted'),
  };
};

/**
 * Translates the MCP suggestions payload into the in-memory state shape used
 * by the suggestion flow. Empty/whitespace fields are skipped so they don't
 * surface as actionable suggestions.
 */
export const buildSuggestionEntries = (
  blob: TravelerProfileSuggestions | undefined,
): SuggestionsState => {
  const state = emptySuggestionsState();
  if (!blob) return state;

  if (blob.travelPreferences) {
    TRAVEL_PREFERENCE_KEYS.forEach((sub) => {
      const value = blob.travelPreferences?.[sub];
      if (!isEmptyString(value)) {
        state.travelPreferences[sub] = { status: 'pending', value: value as string };
      }
    });
  }

  if (blob.significantDates && blob.significantDates.length > 0) {
    state.significantDates = { status: 'pending', value: blob.significantDates };
  }

  TEXT_FIELDS.forEach((field) => {
    const value = blob[field];
    if (!isEmptyString(value)) {
      state.text[field] = { status: 'pending', value: value as string };
    }
  });

  return state;
};

/**
 * Builds the `TravelerProfileDetailsUpdate` partial from accepted entries.
 * Travel preferences are merged onto the loaded profile's preferences so
 * sub-keys not covered by suggestions aren't clobbered.
 */
export const buildCommitPartial = (
  source: SuggestionsState,
  travelerProfile: TravelerProfile | undefined,
): Partial<TravelerProfileDetailsUpdate> => {
  const partial: Partial<TravelerProfileDetailsUpdate> = {};

  const acceptedPrefEntries = (Object.entries(source.travelPreferences) as [
    TravelPreferenceSubKey,
    SuggestionEntry<string> | undefined,
  ][]).filter(([, e]) => e?.status === 'accepted');
  if (acceptedPrefEntries.length > 0) {
    const merged: TravelPreferenceSections = { ...(travelerProfile?.travelPreferences ?? {}) };
    acceptedPrefEntries.forEach(([sub, e]) => {
      if (e) merged[sub] = e.value;
    });
    partial.travelPreferences = merged;
  }

  (Object.entries(source.text) as [SuggestionTextField, SuggestionEntry<string> | undefined][])
    .forEach(([field, e]) => {
      if (e?.status === 'accepted') {
        partial[field] = e.value;
      }
    });

  if (source.significantDates?.status === 'accepted') {
    partial.significantDates = source.significantDates.value;
  }

  return partial;
};
